import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertOctagon, ArrowRight, Bot, CheckSquare, Clock, FileWarning, FolderKanban, Inbox, PenLine, Scale, ShieldAlert, ShieldCheck, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ACTIVITY_LABELS, fmtDate, fmtDateShort, fmtDateTime, fmtMoney, fmtMoneyShort, PHASE_LABELS, PROJECT_HEALTH, SIG6_SEV } from "../lib/labels";
import { userName, useUsers } from "../lib/hooks";
import { Chip, EmptyState, KpiCard, PageHeader, ToneChip, BrandMark } from "../components/common";

function Section({ num, title, subtitle, children, testid }) {
  return (
    <section className="mt-8" data-testid={testid}>
      <div className="mb-3 flex items-baseline gap-3">
        <span className="font-mono text-xs font-semibold text-slate-400">{num}</span>
        <h2 className="ts-section-title">{title}</h2>
        {subtitle && <span className="hidden text-xs text-slate-400 md:inline">{subtitle}</span>}
      </div>
      {children}
    </section>
  );
}

function ItemList({ items, render, linkTo, emptyText = "Nada pendiente. Buen trabajo." }) {
  if (!items?.length) return <p className="py-3 text-xs text-slate-400">{emptyText}</p>;
  return (
    <ul className="divide-y divide-slate-100">
      {items.slice(0, 4).map((it) => (
        <li key={it.id}>
          <Link to={linkTo(it)} className="flex items-center gap-2 py-2 text-sm text-slate-700 transition-colors hover:text-blue-700" data-testid={`dash-item-${it.id}`}>
            <ArrowRight className="h-3 w-3 shrink-0 text-slate-300" />
            <span className="min-w-0 flex-1 truncate">{render(it)}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export default function Dashboard() {
  const { user, area } = useAuth();
  const { map } = useUsers();
  const internal = !user?.is_contractor;
  const { data: d, isLoading } = useQuery({
    queryKey: ["dashboard", area],
    queryFn: async () => (await api.get("/dashboard", { params: { area } })).data,
  });
  const { data: corp } = useQuery({
    queryKey: ["corp-home"],
    queryFn: async () => (await api.get("/corporativo/senales", { params: { status: "nuevo" } })).data,
    enabled: internal,
    retry: false,
  });
  const { data: marina } = useQuery({
    queryKey: ["proyecto", "prj_marina"],
    queryFn: async () => (await api.get("/proyectos/prj_marina")).data,
    enabled: internal,
    retry: false,
  });
  const { data: activity } = useQuery({
    queryKey: ["home-activity"],
    queryFn: async () => (await api.get("/activity", { params: { limit: 8 } })).data,
    enabled: internal,
    retry: false,
  });

  if (isLoading || !d) {
    return <div className="flex h-64 items-center justify-center" data-testid="dashboard-loading"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-700" /></div>;
  }

  const a = d.attention;
  const mp = marina?.project;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={`Hola, ${user.name.split(" ")[0]}`}
        subtitle="¿Dónde debes intervenir? Cada cifra permite profundizar hasta el origen y la evidencia."
        testid="dashboard-title"
      />

      <Section num="01" title="Necesita tu atención" subtitle="Priorizado por el motor certificado: severidad, vencimiento, bloqueo e impacto" testid="section-atencion">
        {corp?.length > 0 && (
          <div className="space-y-2" data-testid="dash-corp-signals">
            {corp.slice(0, 6).map((s) => (
              <Link key={s.id} to={s.action_route || "/control-corporativo"} className="ts-card ts-card-hover flex items-center gap-3 p-3" data-testid={`dash-signal-${s.id}`}>
                <span className={`font-display text-lg font-bold ${s.priority_score >= 60 ? "text-red-700" : s.priority_score >= 35 ? "text-amber-700" : "text-slate-600"}`}>{s.priority_score}</span>
                <Chip map={SIG6_SEV} value={s.severity} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800">{s.title}</p>
                  <p className="truncate text-xs text-slate-500">{s.summary}</p>
                </div>
                <span className="hidden text-right text-xs text-slate-400 md:block">
                  {s.owner_id ? userName(map, s.owner_id) : "sin asignar"}<br />
                  {s.due_date ? `fecha ${String(s.due_date).slice(0, 10)}` : ""}
                </span>
              </Link>
            ))}
            <Link to="/control-corporativo" className="inline-block text-xs font-medium text-blue-700 hover:underline" data-testid="dash-corp-link">Abrir Control corporativo →</Link>
          </div>
        )}
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <KpiCard title="Tareas vencidas" value={a.tasks_overdue} icon={Clock} tone={a.tasks_overdue ? "danger" : "muted"} to="/mi-trabajo?vista=vencido" testid="kpi-tareas-vencidas" />
          <KpiCard title="Evidencias por revisar" value={a.evidence_review} icon={FileWarning} tone={a.evidence_review ? "warning" : "muted"} to="/evidencias?vista=revision" testid="kpi-evidencias-revision" />
          <KpiCard title="Aprobaciones pendientes" value={a.approvals_pending} icon={ShieldCheck} tone={a.approvals_pending ? "ai" : "muted"} to="/aprobaciones?vista=mias" testid="kpi-aprobaciones-pendientes" />
          <KpiCard title="Comunicaciones urgentes" value={a.comms_urgent} icon={Inbox} tone={a.comms_urgent ? "warning" : "muted"} to="/comunicaciones" testid="kpi-comunicaciones-urgentes" />
          <KpiCard title="IA por validar" value={a.ai_validation} icon={Bot} tone={a.ai_validation ? "ai" : "muted"} to="/expedientes?ia=pendiente" testid="kpi-ia-validacion" />
          <KpiCard title="Expedientes bloqueados" value={a.expedientes_blocked} icon={AlertOctagon} tone={a.expedientes_blocked ? "danger" : "muted"} to="/expedientes?estado=bloqueado" testid="kpi-expedientes-bloqueados" />
        </div>
      </Section>

      <Section num="02" title="Decisiones y aprobaciones" subtitle="Decisiones formales que esperan a una persona — ninguna se aprueba sola" testid="section-decisiones">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <KpiCard title="Esperan tu decisión" value={a.approvals_pending} icon={ShieldCheck} tone={a.approvals_pending ? "ai" : "muted"} to="/aprobaciones?vista=mias" testid="kpi-mi-aprobaciones" />
          <KpiCard title="Contratos por validar" value={d.legal?.mi_validacion ?? 0} icon={Scale} tone={d.legal?.mi_validacion ? "warning" : "muted"} to="/legal/contratos?estado=pendiente_validacion" testid="kpi-legal-validacion" />
          <KpiCard title="Firmas en curso" value={d.legal?.firmas_en_curso ?? 0} icon={PenLine} tone={d.legal?.firmas_en_curso ? "info" : "muted"} to="/legal/firmas" testid="kpi-legal-firmas" />
          <KpiCard title="Obligaciones vencidas" value={d.legal?.obligaciones_vencidas ?? 0} icon={AlertOctagon} tone={d.legal?.obligaciones_vencidas ? "danger" : "muted"} to="/legal/obligaciones?estado=vencida" testid="kpi-legal-obligaciones" />
          <KpiCard title="Actas pendientes" value={d.meetings?.actas_pendientes ?? 0} icon={FileWarning} tone={d.meetings?.actas_pendientes ? "warning" : "muted"} to="/reuniones/cuadro-de-mando" testid="kpi-actas-pendientes" />
          <KpiCard title="IA por validar" value={a.ai_validation} icon={Bot} tone={a.ai_validation ? "ai" : "muted"} to="/expedientes?ia=pendiente" testid="kpi-ia-decisiones" />
        </div>
        {a.approvals_pending_items?.length > 0 && (
          <div className="ts-card mt-3 p-4">
            <p className="ts-caption mb-1">Pendiente de tu aprobación</p>
            <ItemList items={a.approvals_pending_items} linkTo={() => "/aprobaciones?vista=mias"} render={(x) => x.subject} />
          </div>
        )}
      </Section>

      <Section num="03" title="Proyectos clave" subtitle="Estado ejecutivo con drill-down a economía, planning y riesgos" testid="section-proyectos">
        {mp && (
          <Link to="/proyectos/prj_marina" className="ts-card ts-card-hover mb-3 block border-l-4 border-l-blue-700 p-5" data-testid="dash-marina-card">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-display text-lg font-bold text-slate-900">{mp.name}</p>
              <Chip map={PROJECT_HEALTH} value={mp.health_status} testid="dash-marina-health" />
              <ToneChip label={PHASE_LABELS[mp.lifecycle_phase]} tone="info" />
              {mp.blocked_reason && <ToneChip label={mp.blocked_reason} tone="danger" />}
            </div>
            <p className="mt-1 text-xs text-slate-500">{mp.municipality}, {mp.region} · cliente {mp.client}</p>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
              <div><p className="ts-caption">Presupuesto</p><p className="font-mono text-sm font-bold text-slate-900" data-testid="dash-marina-budget">{fmtMoneyShort(mp.current_budget)}</p></div>
              <div><p className="ts-caption">Forecast final</p><p className="font-mono text-sm font-bold text-slate-900">{fmtMoneyShort(mp.forecast_final_cost)}</p></div>
              <div><p className="ts-caption">Desviación</p><p className={`font-mono text-sm font-bold ${(mp.variance ?? 0) < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtMoneyShort(mp.variance)}</p></div>
              <div><p className="ts-caption">Avance físico</p><p className="font-mono text-sm font-bold">{mp.physical_progress}%</p></div>
              <div><p className="ts-caption">Fin previsto</p><p className="text-sm font-semibold">{fmtDateShort(mp.current_forecast_completion)}</p></div>
            </div>
          </Link>
        )}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <KpiCard title="Proyectos activos" value={d.projects?.activos ?? 0} icon={FolderKanban} tone="info" to="/proyectos/portafolio" testid="kpi-proj-activos" />
          <KpiCard title="Proyectos bloqueados" value={d.projects?.bloqueados ?? 0} icon={AlertOctagon} tone={d.projects?.bloqueados ? "danger" : "muted"} to="/proyectos/lista?status=bloqueado" testid="kpi-proj-bloqueados" />
          <KpiCard title="Proyectos en riesgo" value={d.projects?.en_riesgo ?? 0} icon={AlertOctagon} tone={d.projects?.en_riesgo ? "warning" : "muted"} to="/proyectos/lista?status=en_riesgo" testid="kpi-proj-riesgo" />
          <KpiCard title="Puertas en revisión" value={d.projects?.gates_en_revision ?? 0} icon={ShieldCheck} tone={d.projects?.gates_en_revision ? "ai" : "muted"} to="/proyectos" testid="kpi-proj-gates" />
        </div>
      </Section>

      <Section num="04" title="Salud operativa" subtitle="Procesos, riesgos, reuniones y áreas" testid="section-salud">
        <div className="grid gap-4 xl:grid-cols-2">
          <div className="ts-card p-4" data-testid="section-procesos">
            <p className="ts-caption mb-2">Control de procesos</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <KpiCard title="Expedientes activos" value={d.process_control.activos} icon={FolderKanban} tone="info" to="/expedientes" testid="kpi-exp-activos" />
              <KpiCard title="Bloqueados" value={d.process_control.bloqueados} icon={AlertOctagon} tone="danger" to="/expedientes?estado=bloqueado" testid="kpi-exp-bloqueados" />
              <KpiCard title="Esperando evidencia" value={d.process_control.esperando_evidencia} icon={FileWarning} tone="warning" to="/expedientes?estado=esperando_evidencia" testid="kpi-exp-evidencia" />
              <KpiCard title="Esperando aprobación" value={d.process_control.esperando_aprobacion} icon={ShieldCheck} tone="ai" to="/expedientes?estado=esperando_aprobacion" testid="kpi-exp-aprobacion" />
              <KpiCard title="Completados (mes)" value={d.process_control.completados_mes} icon={CheckSquare} tone="success" to="/expedientes?estado=completado" testid="kpi-exp-completados" />
            </div>
          </div>
          <div className="ts-card p-4" data-testid="section-riesgos">
            <p className="ts-caption mb-2">Riesgos</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Link to="/expedientes?riesgo=critico" data-testid="kpi-riesgo-critico"><p className="ts-caption">Críticos</p><p className="font-display text-2xl font-bold text-red-600">{d.risks.criticos}</p></Link>
              <Link to="/expedientes?riesgo=alto" data-testid="kpi-riesgo-alto"><p className="ts-caption">Altos</p><p className="font-display text-2xl font-bold text-amber-600">{d.risks.altos}</p></Link>
              <Link to="/expedientes?estado=bloqueado" data-testid="kpi-riesgo-vencidos"><p className="ts-caption">Vencidos</p><p className="font-display text-2xl font-bold text-slate-700">{d.risks.vencidos}</p></Link>
              <Link to="/evidencias?estado=rechazada" data-testid="kpi-ev-rechazadas"><p className="ts-caption">Evidencias rechazadas</p><p className="font-display text-2xl font-bold text-slate-700">{d.risks.evidencias_rechazadas}</p></Link>
            </div>
            <div className="mt-3 border-t border-slate-100 pt-2">
              <ItemList items={d.risks.criticos_items} linkTo={(e) => `/expedientes/${e.id}`} render={(e) => `${e.code} — ${e.title}`} emptyText="Sin expedientes de riesgo crítico abiertos." />
            </div>
          </div>
          <div className="ts-card p-4" data-testid="section-reuniones">
            <p className="ts-caption mb-2">Reuniones y ejecución de lo decidido</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <KpiCard title="Reuniones hoy" value={d.meetings?.reuniones_hoy ?? 0} icon={Clock} tone={d.meetings?.reuniones_hoy ? "info" : "muted"} to="/reuniones" testid="kpi-reuniones-hoy" />
              <KpiCard title="Acciones vencidas" value={d.meetings?.acciones_vencidas ?? 0} icon={Clock} tone={d.meetings?.acciones_vencidas ? "danger" : "muted"} to="/reuniones/cuadro-de-mando" testid="kpi-acciones-vencidas" />
              <KpiCard title="Decisiones bloqueadas" value={d.meetings?.decisiones_bloqueadas ?? 0} icon={AlertOctagon} tone={d.meetings?.decisiones_bloqueadas ? "danger" : "muted"} to="/reuniones/cuadro-de-mando" testid="kpi-decisiones-bloqueadas" />
              <KpiCard title="Compromisos incumplidos" value={d.meetings?.compromisos_incumplidos ?? 0} icon={Scale} tone={d.meetings?.compromisos_incumplidos ? "danger" : "muted"} to="/reuniones/cuadro-de-mando" testid="kpi-compromisos-incumplidos" />
            </div>
          </div>
          <div className="ts-card p-4" data-testid="section-ia">
            <p className="ts-caption mb-2">IA gobernada (propone · humano valida)</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Link to="/expedientes?ia=pendiente" data-testid="kpi-ia-pendientes"><p className="ts-caption">Pendientes de validación</p><p className="font-display text-2xl font-bold text-violet-600">{d.ai.pendientes_validacion}</p></Link>
              <Link to="/expedientes?ia=pendiente" data-testid="kpi-ia-baja-confianza"><p className="ts-caption">Baja confianza</p><p className="font-display text-2xl font-bold text-red-600">{d.ai.baja_confianza}</p></Link>
              <div><p className="ts-caption">Con hallazgos de riesgo</p><p className="font-display text-2xl font-bold text-amber-600">{d.ai.hallazgos_riesgo}</p></div>
              <div><p className="ts-caption">Sin acción</p><p className="font-display text-2xl font-bold text-slate-500">{d.ai.sin_accion}</p></div>
            </div>
          </div>
        </div>
      </Section>

      <div className="grid gap-6 xl:grid-cols-2">
        <Section num="05" title="Qué ha cambiado" subtitle="Desde tu último acceso" testid="section-cambios">
          <div className="ts-card max-h-72 overflow-y-auto p-4">
            {!d.changes.length ? (
              <EmptyState title="Sin cambios desde tu último acceso" description="Cuando alguien complete tareas, aporte evidencias o decida aprobaciones, lo verás aquí." testid="changes-empty" />
            ) : (
              <ul className="space-y-2.5">
                {d.changes.map((c) => (
                  <li key={c.id} className="flex items-start gap-2 text-sm" data-testid={`change-${c.id}`}>
                    <TrendingUp className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300" />
                    <div className="min-w-0">
                      <span className="font-medium text-slate-800">{ACTIVITY_LABELS[c.action] || c.action}</span>
                      <span className="text-slate-600"> — {c.detail}</span>
                      <p className="text-xs text-slate-400">{userName(map, c.actor_id)} · {fmtDateTime(c.timestamp)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Section>

        <Section num="06" title="Actividad reciente" subtitle="Últimos movimientos del sistema" testid="section-actividad">
          <div className="ts-card max-h-72 overflow-y-auto p-4">
            {internal && activity?.length ? (
              <ul className="space-y-2.5">
                {activity.map((c) => (
                  <li key={c.id} className="flex items-start gap-2 text-sm" data-testid={`activity-${c.id}`}>
                    <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300" />
                    <div className="min-w-0">
                      <span className="font-medium text-slate-800">{ACTIVITY_LABELS[c.action] || c.action}</span>
                      <span className="text-slate-600"> — {c.detail}</span>
                      <p className="text-xs text-slate-400">{userName(map, c.actor_id)} · {fmtDateTime(c.timestamp)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="Sin actividad reciente" description="La actividad del equipo aparecerá aquí en cuanto se produzcan movimientos." testid="activity-empty" />
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}
